'use client';

import type { ReactNode } from 'react';

import { Icon } from '../icon';
import type { Size } from '../shared.types';
import { Tooltip, TooltipContent, TooltipTrigger } from './Tooltip';
import type { TooltipProps } from './Tooltip.types';

type IconTooltipProps = Pick<TooltipProps, 'placement' | 'style'> & {
  icon: ReactNode;
  /**
   * The icon's accessible name, and the tooltip's text when no children are given. An
   * `Icon` asserts no semantics of its own, so this is what names it.
   */
  label: string;
  size?: Size;
  className?: string;
  contentClassName?: string;
  /** Drops the padding around the tooltip's content; see `TooltipContent`. */
  contentFlush?: boolean;
  children?: ReactNode;
};

/**
 * An icon that says what it is on hover. The label is repeated as the tooltip's text
 * unless something richer is passed as children — and when it is repeated the tooltip
 * is hidden from assistive technology, which has already been given the same words as
 * the icon's name.
 */
function IconTooltip({
  icon,
  label,
  placement = 'top',
  size,
  style,
  className,
  contentClassName,
  contentFlush,
  children,
}: IconTooltipProps) {
  return (
    <Tooltip placement={placement}>
      <TooltipTrigger asChild={true}>
        <Icon
          icon={icon}
          size={size}
          style={style}
          className={className}
          role="img"
          aria-label={label}
        />
      </TooltipTrigger>
      <TooltipContent
        className={contentClassName}
        flush={contentFlush}
        aria-hidden={children === undefined}
      >
        {children ?? label}
      </TooltipContent>
    </Tooltip>
  );
}

export { IconTooltip };
export type { IconTooltipProps };
